import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

// ============================================================================
// InfoPopover — the ⓘ affordance used across the Team & Roles surface.
//
// Replaces native `title` tooltips (invisible on touch, unstyled, truncating)
// with a real anchored popover: opens on click, dismisses on outside click /
// Escape, and is keyboard focusable. The panel portals to document.body with
// viewport-fixed coordinates so it is never clipped by the modal's scroll
// containers.
// ============================================================================

const POPOVER_WIDTH = 300;

const Wrap = styled.span`
  display: inline-flex;
  flex-shrink: 0;
`;

const InfoBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  border-radius: var(--radius-round);
  background: none;
  color: var(--color-text-tertiary);
  font-family: var(--font-family-text);
  font-size: 0.75rem;
  line-height: 1;
  cursor: pointer;
  transition: color var(--transition-fast), background var(--transition-fast);

  &:hover {
    color: var(--color-accent-text);
    background: var(--color-accent-subtle);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const Panel = styled.div`
  position: fixed;
  z-index: 20000010; /* above the role editor modal (20000000) */
  width: ${POPOVER_WIDTH}px;
  max-width: calc(100vw - 24px);
  background: var(--color-background-primary);
  border: 1px solid var(--color-border-medium);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  padding: var(--spacing-sm) var(--spacing-md);
  font-family: var(--font-family-text);
  font-size: 0.75rem;
  font-weight: 400;
  color: var(--color-text-secondary);
  line-height: 1.5;
  text-align: left;

  &:focus {
    outline: none;
  }
`;

interface InfoPopoverProps {
  /** Accessible name for the trigger AND the dialog, e.g. `more about view roles`. */
  label: string;
  /** Popover body. */
  children: React.ReactNode;
}

const InfoPopover: React.FC<InfoPopoverProps> = ({ label, children }) => {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const open = pos !== null;

  const openAtButton = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    // Clamp horizontally so the panel never leaves the viewport.
    const left = Math.max(12, Math.min(r.left, window.innerWidth - POPOVER_WIDTH - 12));
    setPos({ top: r.bottom + 6, left });
  };

  useEffect(() => {
    if (!open) return;
    panelRef.current?.focus();
    const onPointerDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setPos(null);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setPos(null);
        btnRef.current?.focus();
      }
    };
    // The anchor moves with any scroll (modal panes scroll independently) —
    // closing is honest; re-anchoring mid-scroll is jitter.
    const onScrollOrResize = () => setPos(null);
    document.addEventListener('mousedown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  return (
    <Wrap>
      <InfoBtn
        ref={btnRef}
        type="button"
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={(e) => {
          // Inside <label> wrappers a click would also toggle the checkbox.
          e.preventDefault();
          e.stopPropagation();
          if (open) setPos(null);
          else openAtButton();
        }}
      >
        ⓘ
      </InfoBtn>
      {open && typeof document !== 'undefined' &&
        ReactDOM.createPortal(
          <Panel
            ref={panelRef}
            role="dialog"
            aria-label={label}
            tabIndex={-1}
            style={{ top: pos!.top, left: pos!.left }}
          >
            {children}
          </Panel>,
          document.body
        )}
    </Wrap>
  );
};

export default InfoPopover;
